import React from 'react';

/////Local Shared Types//////
import {LogoSize, ResourcePageRoutes, ResourcePages} from 'app/types/Shared';
import smallLogo from 'assets/images/header/elevateLogoSmall.svg';
import takedaLogo from 'assets/images/header/takedaLogo.svg';
/////React Router Dom/////
import {NavLink, useNavigate, useParams} from 'react-router-dom';
///Local Components//////
import PageLayout from 'components/PageLayout/PageLayout';
import TopNavigation from 'components/TopNavigation/TopNavigation';
import ChangeRole from 'components/ChangeRole/ChangeRole';
import {Breadcrumbs, Box, Link, Grid, Typography, Stack, LinearProgress, useTheme} from '@mui/material';

import {useMatomo} from '@jonkoops/matomo-tracker-react';
import {useSelector} from 'react-redux';
import {selectOnboarding} from 'app/store/appState/appStateSlice';
import ResourceGrid from 'components/ResourceGrid/ResourceGrid';
import PartnershipPage from 'components/PartnershipPage/PartnershipPage';

const ResourcePage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const onboarding = useSelector(selectOnboarding);
  const {resourceSlug} = useParams<{resourceSlug?: string}>();
  const [pageType, setPageType] = React.useState<ResourcePages>();

  // start at top of page when navigating to a new page inside resources library
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [resourceSlug]);

  React.useEffect(() => {
    switch (resourceSlug) {
      case undefined:
        setPageType(ResourcePages.Resources);
        return;
      case ResourcePageRoutes.Partnerships:
        setPageType(ResourcePages.Partnerships);
        return;
      default:
        console.log('Page not found');
        navigate('/404');
        return;
    }
  }, [resourceSlug, navigate]);

  // Matomo Analytics
  const {trackPageView} = useMatomo();
  React.useEffect(() => {
    trackPageView();
  });

  //Callback for TopNavigation Logo/Home Button
  function navigateHome() {
    navigate('/home');
    return Promise.resolve();
  }

  const onLogout = async () => {
    navigate('/login');
    return Promise.resolve();
  };

  const onSearch = (query?: string) => {
    navigate(`/search?q=${query}`);
    return Promise.resolve();
  };

  const toResources = () => {
    navigate(`/resources`);
    return Promise.resolve();
  };

  const renderResourcePage = (selected: ResourcePages) => {
    switch (selected) {
      case ResourcePages.Resources:
        return <ResourceGrid />;
      case ResourcePages.Partnerships:
        return <PartnershipPage />;
      default:
        return <></>;
    }
  };

  const header = (
    <TopNavigation
      authorized={true}
      logout={onLogout}
      smallLogo={smallLogo}
      rightLogo={takedaLogo}
      LogoType={LogoSize.Small}
      home={navigateHome}
      search={onSearch}
      toResources={toResources}
      banner={onboarding?.topBanner}
    />
  );

  const footer = <></>;
  return (
    <>
      {pageType ? (
        <PageLayout header={header} footer={footer}>
          <Grid
            justifyContent='flex-start'
            alignItems='flex-end'
            container
            direction='row'
            spacing={1}
            px={6}
            marginTop={onboarding?.topBanner ? 7.5 : 2.5}
          >
            <Grid item xs={3}>
              <Breadcrumbs
                sx={{
                  '.MuiBreadcrumbs-separator': {
                    color: 'primary.main',
                  },
                }}
              >
                <Link underline='hover' color='primary.main' component={NavLink} to='/home'>
                  Home
                </Link>
                {pageType !== ResourcePages.Resources && (
                  <Link underline='hover' color='primary.main' component={NavLink} to='/resources'>
                    Resources
                  </Link>
                )}
                <Typography color='primary.main'>{pageType}</Typography>
              </Breadcrumbs>
            </Grid>
            <Grid item xs={9} minHeight='70px' display='flex' justifyContent='flex-end'>
              <ChangeRole />
            </Grid>
            <Grid item xs={12}>
              <Stack spacing={2} my={2} pb='10px'>
                <Typography variant='h1'>{pageType}</Typography>
              </Stack>
            </Grid>
          </Grid>

          <Box
            sx={{
              borderTop: `1px solid ${theme.palette.grey[500]}`,
              width: '100%',
              bgcolor: theme.palette.grey[100],
              minHeight: '99vh',
              flexGrow: 1,
              flexDirection: 'column',
            }}
          >
            {renderResourcePage(pageType)}
          </Box>
        </PageLayout>
      ) : (
        <Box sx={{pt: 10, width: '100%'}}>
          <LinearProgress color='secondary' />
        </Box>
      )}
    </>
  );
};

export default ResourcePage;
